/**
 * Org-aware navigation helpers.
 *
 * Every authenticated app URL lives under `/:orgPublicId/…` where
 * `orgPublicId` is the 8-digit opaque identifier for the active
 * organization (see migration 0013). Using an opaque ID rather than
 * a name-derived slug keeps customer identities private in pasted
 * links — the URL alone doesn't tell a competitor which company the
 * recipient's recipient works for.
 *
 * Call sites use `navigate("/ads")` with a leading slash as normal;
 * `useOrgNavigate` / `useOrgPath` prepend the public_id so the URL
 * ends up as `/10384729/ads`. A tiny helper set is less error-prone
 * than auditing every link for relative vs absolute.
 */

import { useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "./useAuth";

/**
 * Active org's public_id as a string (URL params are strings), taken
 * from the URL when we're inside a matched org route, or from the
 * logged-in user's active org otherwise (post-login redirect).
 * Returns null when neither source has it yet.
 */
export function useOrgPublicId(): string | null {
  const params = useParams<{ orgPublicId?: string }>();
  const { user } = useAuth();
  if (params.orgPublicId) return params.orgPublicId;
  if (user?.org_public_id != null) return String(user.org_public_id);
  return null;
}

/**
 * Build an absolute path under the active org's public_id. Accepts
 * both leading-slash and leading-slash-free inputs. External URLs
 * short-circuit unchanged.
 */
export function useOrgPath(): (path: string) => string {
  const publicId = useOrgPublicId();
  return useCallback(
    (path: string) => {
      if (/^https?:\/\//i.test(path)) return path;
      if (!publicId) return path;
      const normalized = path.startsWith("/") ? path : `/${path}`;
      return `/${publicId}${normalized}`;
    },
    [publicId]
  );
}

/**
 * Drop-in replacement for react-router's `useNavigate` that auto-
 * prepends the active org's public_id. Used by all in-app routing
 * (Sidebar, post-row click, etc.). For auth pages (/login,
 * /forgot-password) use react-router's `useNavigate` directly — those live
 * outside the org-scoped tree.
 */
export function useOrgNavigate(): (path: string, options?: { replace?: boolean }) => void {
  const navigate = useNavigate();
  const orgPath = useOrgPath();
  return useCallback(
    (path, options) => navigate(orgPath(path), options),
    [navigate, orgPath]
  );
}

/**
 * Strip the leading `/:orgPublicId` segment from the current URL.
 * Used by the org switcher: after the user picks a different org,
 * we want to land them on the same screen under the new public_id
 * (`/10384729/ads/campaigns/42` → `/20915630/ads/campaigns/42`).
 */
export function usePathWithoutPublicId(): string {
  const location = useLocation();
  const publicId = useOrgPublicId();
  if (!publicId) return location.pathname + location.search;
  const prefix = `/${publicId}`;
  const pathname = location.pathname.startsWith(prefix)
    ? location.pathname.slice(prefix.length) || "/"
    : location.pathname;
  return pathname + location.search;
}
